import './index-view-toggle.scss';
import Base from '../base/base';
import { debounce } from '../../shared/utils/helpers';
import { BREAKPOINTS } from '../../shared/utils/variables';

export type IndexView = 'list' | 'map';

/** Icons read as the destination of a tap (Map while listing, List while mapping) */
const MAP_ICON =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>' +
  '<line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>';

const LIST_ICON =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>' +
  '<line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>' +
  '<line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>';

interface IndexViewToggleOptions {
  /** Selector for the element the map-view state class is toggled on */
  containerSelector?: string;
  /** Selector of the fixed header whose height fills `--nav-h` on :root */
  navSelector?: string;
  /** Viewport width (px) at or below which the toggle is offered. Above it,
   *  the page is expected to show list + map together, so the toggle hides and
   *  any active map view is reset to list. Defaults to BREAKPOINTS.mobile. */
  maxWidth?: number;
  /** Class applied to the container while map view is active */
  mapViewClass?: string;
  /** Button label shown while map view is active (tapping returns to the list) */
  listLabel?: string;
  /** Button label shown while list view is active (tapping shows the map) */
  mapLabel?: string;
  /** Lock body scroll while map view is active, so the map owns touch gestures */
  lockScroll?: boolean;
  /** Called with the new view whenever it changes (e.g. to activate the map) */
  onChange?: (view: IndexView) => void;
  debug?: boolean;
  [key: string]: any;
}

interface IndexViewToggleConfig {
  containerSelector: string;
  navSelector: string;
  maxWidth: number;
  mapViewClass: string;
  listLabel: string;
  mapLabel: string;
  lockScroll: boolean;
  onChange?: (view: IndexView) => void;
}

/**
 * IndexViewToggle
 *
 * A mobile-only floating button that swaps a collection page between its list
 * and a map (the Zillow pattern). It owns no layout of its own beyond the
 * button: it toggles a single state class (`mapViewClass`) on a container and
 * reports the active view through `onChange`; the page's stylesheet keys off
 * that class to hide the list and reveal the map. Pair the `onChange` with
 * IndexMap.activate() so the map is created/resized the moment it shows
 * (a phone in list view never pays for the map otherwise).
 *
 * The toggle is offered only at or below `maxWidth` (default BREAKPOINTS.mobile)
 * — the breakpoint where such pages typically collapse to a single column with
 * the map hidden. Above it the button hides and any active map view resets to
 * list, so a viewport growing past the breakpoint can't get stuck in the
 * mobile map overlay.
 */
export default class IndexViewToggle extends Base {
  static readonly BUTTON_CLASS = 'cvt';
  /** Applied to the button while the toggle is offered (mobile) */
  static readonly ENABLED_CLASS = 'cvt--enabled';

  private config: IndexViewToggleConfig;

  private container: HTMLElement | null = null;
  private button: HTMLButtonElement | null = null;
  private iconEl: HTMLElement | null = null;
  private labelEl: HTMLElement | null = null;

  private view: IndexView = 'list';
  private enabled = false;
  private destroyed = false;
  private prevBodyOverflow = '';
  private prevScrollY = 0;

  private mediaQuery: MediaQueryList | null = null;
  private mediaHandler: (() => void) | null = null;
  private navObserver: ResizeObserver | null = null;
  private resizeHandler: (() => void) | null = null;
  private clickHandler: (() => void) | null = null;

  constructor(options: IndexViewToggleOptions = {}) {
    super(options.debug || false);

    this.config = {
      containerSelector: '.map-page-container',
      navSelector: '.nav',
      maxWidth: BREAKPOINTS.mobile,
      mapViewClass: 'view-map',
      listLabel: 'List View',
      mapLabel: 'Map View',
      lockScroll: true,
      ...options
    };

    this.init();
  }

  init(): void {
    this.container = document.querySelector<HTMLElement>(this.config.containerSelector);
    if (!this.container) {
      this.log('Container not found, skipping', this.config.containerSelector);
      return;
    }

    this.buildButton();
    this.initNavMeasure();
    this.initResponsive();

    this.log('Initialized');
  }

  /** The active view */
  getView(): IndexView {
    return this.view;
  }

  /* ===================
     Button
     =================== */

  private buildButton(): void {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = IndexViewToggle.BUTTON_CLASS;

    this.iconEl = document.createElement('span');
    this.iconEl.className = 'cvt__icon';

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'cvt__label';

    button.appendChild(this.iconEl);
    button.appendChild(this.labelEl);

    this.clickHandler = () => this.toggle();
    button.addEventListener('click', this.clickHandler);

    // Body-level so the fixed button isn't clipped by the map's overflow:hidden
    document.body.appendChild(button);
    this.button = button;

    this.renderButton();
  }

  /** Reflect the current view: the button advertises the view a tap switches to */
  private renderButton(): void {
    if (!this.button || !this.iconEl || !this.labelEl) return;

    const toMap = this.view === 'list';
    this.iconEl.innerHTML = toMap ? MAP_ICON : LIST_ICON;
    this.labelEl.textContent = toMap ? this.config.mapLabel : this.config.listLabel;
    this.button.setAttribute(
      'aria-label',
      toMap ? `Show ${this.config.mapLabel} view` : `Show ${this.config.listLabel} view`
    );
    this.button.setAttribute('aria-pressed', this.view === 'map' ? 'true' : 'false');
  }

  private toggle(): void {
    if (!this.enabled) return; // ignore stray clicks while the toggle is hidden
    this.setView(this.view === 'map' ? 'list' : 'map');
  }

  /* ===================
     View state
     =================== */

  private setView(view: IndexView, emit: boolean = true): void {
    if (this.destroyed || view === this.view) return;
    const mapActive = view === 'map';

    // Capture the scroll position before the class toggle collapses the page:
    // hiding the list shrinks the document and the browser clamps scrollY, so
    // reading it afterward would lose where the user actually was.
    if (mapActive) this.prevScrollY = window.scrollY;

    this.view = view;

    // Re-measure before the map shows so its height calc uses a current nav.
    if (mapActive) this.measureNav();

    this.container?.classList.toggle(this.config.mapViewClass, mapActive);
    this.renderButton();

    if (mapActive) {
      // The map is pinned to the top of the content, so entering map view while
      // scrolled down would leave it above the viewport — and the scroll lock
      // would then freeze it there, off-screen. Jump to the top first.
      window.scrollTo(0, 0);
      if (this.config.lockScroll) {
        this.prevBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Unlock first (the page must be scrollable again), then drop the user
      // back where they were before they opened the map.
      if (this.config.lockScroll) {
        document.body.style.overflow = this.prevBodyOverflow;
      }
      window.scrollTo(0, this.prevScrollY);
    }

    this.log(`View set to ${view}`);
    if (emit) this.config.onChange?.(view);
  }

  /* ===================
     Responsive enable/disable
     =================== */

  private initResponsive(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      this.setEnabled(true); // no media support: leave the toggle usable
      return;
    }

    this.mediaQuery = window.matchMedia(`(max-width: ${this.config.maxWidth}px)`);
    this.mediaHandler = () => this.setEnabled(this.mediaQuery!.matches);
    this.mediaQuery.addEventListener('change', this.mediaHandler);

    this.setEnabled(this.mediaQuery.matches);
  }

  private setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.button?.classList.toggle(IndexViewToggle.ENABLED_CLASS, enabled);

    // Growing past the breakpoint reveals list + map together, so never leave
    // the page stuck in the mobile map overlay.
    if (!enabled && this.view === 'map') this.setView('list');
  }

  /* ===================
     Nav height (--nav-h)
     =================== */

  private initNavMeasure(): void {
    this.measureNav();

    const nav = document.querySelector<HTMLElement>(this.config.navSelector);
    if (nav && typeof ResizeObserver !== 'undefined') {
      this.navObserver = new ResizeObserver(() => this.measureNav());
      this.navObserver.observe(nav);
    }

    this.resizeHandler = debounce(() => this.measureNav(), 150);
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Published on the document element rather than the container, because the
   * consumers are not all descendants of it: the export's sticky
   * `.toggle-header` is a SIBLING of this container, and the filter sheet
   * lives on document.body. One measurement, readable from anywhere.
   */
  private measureNav(): void {
    const nav = document.querySelector<HTMLElement>(this.config.navSelector);
    const height = nav ? nav.offsetHeight : 0;
    document.documentElement.style.setProperty('--nav-h', `${height}px`);
  }

  /* ===================
     Cleanup
     =================== */

  destroy(): void {
    this.destroyed = true;

    if (this.button && this.clickHandler) {
      this.button.removeEventListener('click', this.clickHandler);
    }
    this.clickHandler = null;

    if (this.mediaQuery && this.mediaHandler) {
      this.mediaQuery.removeEventListener('change', this.mediaHandler);
    }
    this.mediaQuery = null;
    this.mediaHandler = null;

    this.navObserver?.disconnect();
    this.navObserver = null;

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    // Torn down while in map view: undo the scroll lock and put the user back.
    if (this.view === 'map') {
      if (this.config.lockScroll) document.body.style.overflow = this.prevBodyOverflow;
      window.scrollTo(0, this.prevScrollY);
    }

    if (this.container) {
      this.container.classList.remove(this.config.mapViewClass);
    }
    document.documentElement.style.removeProperty('--nav-h');

    this.button?.remove();
    this.button = null;
    this.iconEl = null;
    this.labelEl = null;
    this.view = 'list';
  }
}
